import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AuthField from "../lotto/AuthField";
import SignUpSuccessDialog from "./SignUpSuccessDialog";
import { useCustomColors } from "../../hooks/useCustomColors";
import { Bank, ReferralCodeResponse, SignUpParams, SignUpResponse, checkReferralCode, getBankList, postSignUp } from "../../api";
import { PATTERNS, secureString } from "../../utils/validation";
import { useSecureInput } from "../../hooks/useSecureInput";
import { useContact } from "../../hooks/useContact";
import { getAuthErrorMessage } from "../../utils/apiError";

type SignUpFormValues = SignUpParams & { password_confirmation: string };

type ReferralCheck =
  | { status: "idle" }
  | { status: "checking" | "valid" | "invalid" | "error"; code: string };

const formatBankAccount = (value: string) =>
  value
    .replace(/\D/g, "")
    .slice(0, 20)
    .replace(/(\d{4})(?=\d)/g, "$1 ");

interface SignUpFormProps {
  /** Fired once registration succeeds and the user dismisses the pending
   *  notice. The mobile dialog uses it to switch across to Login. */
  onSuccess?: () => void;
}

const SignUpForm = ({ onSuccess }: SignUpFormProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const colors = useCustomColors();
  const { reviewUrl } = useContact();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [banks, setBanks] = useState<Bank[]>([]);
  /* The code is carried alongside the verdict so a result can never be read as
     the answer for a code the user has since edited. */
  const [referralCheck, setReferralCheck] = useState<ReferralCheck>({ status: "idle" });
  const referralResults = useRef(new Map<string, ReferralCheck>());
  const referralRequests = useRef(new Map<string, Promise<ReferralCodeResponse>>());
  const [result, setResult] = useState<{
    message: string;
    data: SignUpResponse;
  } | null>(null);

  const signUpSchema = Yup.object().shape({
    referral_code: secureString(t, { maxLength: 20 })
      .required(t("validation_referral_required"))
      .matches(PATTERNS.code, t("validation_referral_format")),
    username: secureString(t, { maxLength: 20 })
      .required(t("validation_username_required"))
      .min(4, t("validation_username_min"))
      .matches(PATTERNS.username, t("validation_username_format")),
    password: secureString(t, { kind: "password", maxLength: 64 })
      .required(t("validation_password_required"))
      .min(8, t("validation_password_min"))
      .matches(PATTERNS.password, t("validation_password_format")),
    password_confirmation: secureString(t, { kind: "password", maxLength: 64 })
      .required(t("validation_confirm_password_required"))
      .oneOf([Yup.ref("password")], t("validation_password_not_match")),
    full_name: secureString(t, { maxLength: 60 })
      .required(t("validation_name_required"))
      .min(3, t("validation_name_min"))
      .matches(PATTERNS.name, t("validation_name_format")),
    phone_no: secureString(t, { maxLength: 15 })
      .required(t("validation_phone_required"))
      .matches(PATTERNS.phone, t("validation_phone_invalid")),
    bank_name: Yup.string().required(t("validation_bank_required")),
    bank_account_no: Yup.string()
      .required(t("validation_bank_account_required"))
      .matches(/^\d{6,20}$/, t("validation_bank_account_invalid")),
  });

  useEffect(() => {
    getBankList()
      .then((response) => setBanks(response.status === 1 ? response.data : []))
      .catch(() => setBanks([]));
  }, []);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: yupResolver(signUpSchema),
    defaultValues: {
      referral_code: "",
      username: "",
      password: "",
      password_confirmation: "",
      full_name: "",
      phone_no: "",
      bank_name: "",
      bank_account_no: "",
    },
  });

  const referral = useSecureInput({
    name: "referral_code",
    control,
    maxLength: 20,
    onChange: () => setReferralCheck({ status: "idle" }),
    onBlur: (code) => void ensureReferralChecked(code),
  });
  const username = useSecureInput({ name: "username", control, maxLength: 20 });
  const password = useSecureInput({ name: "password", control, kind: "password", maxLength: 64 });
  const passwordConfirmation = useSecureInput({
    name: "password_confirmation",
    control,
    kind: "password",
    maxLength: 64,
  });
  const fullName = useSecureInput({ name: "full_name", control, maxLength: 60 });
  const phone = useSecureInput({ name: "phone_no", control, maxLength: 15 });

  /* One request per distinct code. A resolved verdict is cached, and a code
     already in flight is awaited rather than re-requested, so leaving and
     re-entering the field costs nothing. Failures are not cached — a network
     error should be retryable, which is also why it is its own status and not
     folded into "invalid". */
  const ensureReferralChecked = async (rawCode: string): Promise<ReferralCheck> => {
    const code = rawCode.trim();

    if (!code || !PATTERNS.code.test(code)) {
      setReferralCheck({ status: "idle" });
      return { status: "idle" };
    }

    const cached = referralResults.current.get(code);
    if (cached) {
      setReferralCheck(cached);
      return cached;
    }

    setReferralCheck({ status: "checking", code });

    let request = referralRequests.current.get(code);
    if (!request) {
      request = checkReferralCode(code);
      referralRequests.current.set(code, request);
    }

    let result: ReferralCheck;
    try {
      const response = await request;
      result = { status: response.valid ? "valid" : "invalid", code };
      referralResults.current.set(code, result);
    } catch {
      result = { status: "error", code };
    } finally {
      referralRequests.current.delete(code);
    }

    // Only paint it if the field still holds the code this answer is about.
    setReferralCheck((current) =>
      current.status !== "idle" && current.code === code ? result : current
    );
    return result;
  };

  const onSubmit = async (values: SignUpFormValues) => {
    /* Blur may not have run — submitting straight from the field via Enter
       skips it — and an older code's verdict must never stand in for this one,
       so the check is re-confirmed here. Cached codes resolve without a call. */
    const check = await ensureReferralChecked(values.referral_code);
    if (check.status === "error") {
      toast.error(t("toast_referral_check_failed"));
      return;
    }
    if (check.status !== "valid") return;

    setIsLoading(true);
    try {
      // The language the form was filled in, so the server's review notice
      // comes back in it rather than in English beside translated chrome.
      const response = await postSignUp(values, i18n.language === "zh" ? "zh" : "en");

      if (response.status === 1) {
        // Admin approves manually — stay put and surface the API message.
        setResult({ message: response.message, data: response.data });
      } else {
        toast.error(t("toast_sign_up_failed"));
      }
    } catch (error) {
      toast.error(getAuthErrorMessage(error, t, "toast_sign_up_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <AuthField
        label={t("sign_up_referral_code")}
        icon={ConfirmationNumberRoundedIcon}
        placeholder={t("sign_up_referral_code_placeholder")}
        {...referral}
        error={
          referral.error ||
          referralCheck.status === "invalid" ||
          referralCheck.status === "error"
        }
        helperText={
          referral.helperText ||
          (referralCheck.status === "checking"
            ? t("validation_referral_checking")
            : referralCheck.status === "invalid"
            ? t("validation_referral_invalid")
            : referralCheck.status === "error"
            ? t("validation_referral_check_failed")
            : undefined)
        }
      />
      <AuthField
        label={t("sign_up_username")}
        icon={PersonRoundedIcon}
        placeholder={t("sign_up_username_placeholder")}
        {...username}
      />
      <AuthField
        label={t("sign_up_password")}
        icon={LockRoundedIcon}
        type={showPassword ? "text" : "password"}
        placeholder={t("sign_up_password_placeholder")}
        {...password}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                sx={{ color: colors.text.secondary }}
              >
                {showPassword ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <AuthField
        label={t("sign_up_confirm_password")}
        icon={LockRoundedIcon}
        type={showConfirmPassword ? "text" : "password"}
        placeholder={t("sign_up_confirm_password_placeholder")}
        {...passwordConfirmation}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
                sx={{ color: colors.text.secondary }}
              >
                {showConfirmPassword ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <AuthField
        label={t("sign_up_name")}
        icon={BadgeRoundedIcon}
        placeholder={t("sign_up_name_placeholder")}
        {...fullName}
      />
      <AuthField
        label={t("sign_up_phone")}
        icon={PhoneRoundedIcon}
        placeholder={t("sign_up_phone_placeholder")}
        {...phone}
      />
      <AuthField
        select
        label={t("sign_up_bank")}
        icon={AccountBalanceRoundedIcon}
        defaultValue=""
        {...register("bank_name")}
        error={!!errors.bank_name}
        helperText={errors.bank_name?.message}
        SelectProps={{
          displayEmpty: true,
          MenuProps: {
            PaperProps: {
              sx: {
                maxHeight: "296px",
                backgroundColor: colors.background.panelSecondary,
                border: `1px solid ${colors.border.accent}`,
                "& .MuiMenuItem-root": {
                  color: colors.text.primary,
                  "&:hover": { backgroundColor: colors.background.rail },
                  "&.Mui-selected": {
                    backgroundColor: colors.tint.primaryStrong,
                    "&:hover": { backgroundColor: colors.tint.primaryStrong },
                  },
                },
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "6px",
                  backgroundColor: colors.border.focus,
                },
              },
            },
          },
          renderValue: (value) =>
            value ? (
              (value as string)
            ) : (
              <Box component="span" sx={{ color: colors.text.disabled }}>
                {t("sign_up_bank_placeholder")}
              </Box>
            ),
        }}
      >
        {banks.map((bank) => (
          <MenuItem key={bank.code} value={bank.name}>
            {bank.name}
          </MenuItem>
        ))}
      </AuthField>
      <Controller
        name="bank_account_no"
        control={control}
        render={({ field }) => (
          <AuthField
            label={t("sign_up_bank_account")}
            icon={CreditCardRoundedIcon}
            placeholder={t("sign_up_bank_account_placeholder")}
            name={field.name}
            value={formatBankAccount(field.value || "")}
            onChange={(event) => {
              field.onChange(event.target.value.replace(/\D/g, "").slice(0, 20));
            }}
            onBlur={field.onBlur}
            inputRef={field.ref}
            inputProps={{ inputMode: "numeric", maxLength: 24 }}
            error={!!errors.bank_account_no}
            helperText={errors.bank_account_no?.message}
          />
        )}
      />

      <Button
        type="submit"
        fullWidth
        disabled={isLoading}
        sx={{ mt: "8px", height: "52px", borderRadius: "14px" }}
      >
        {isLoading ? (
          <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
            <CircularProgress size={18} color="inherit" />
            {t("sign_up_registering")}
          </Stack>
        ) : (
          t("sign_up_submit")
        )}
      </Button>
    </Box>

    <SignUpSuccessDialog
      open={!!result}
      onClose={() => setResult(null)}
      username={result?.data.username ?? ""}
      notice={result?.message}
      reviewUrl={reviewUrl}
      onContinue={() => (onSuccess ? onSuccess() : navigate("/login"))}
    />

    <ToastContainer
      position="top-right"
      autoClose={3000}
      closeOnClick
      pauseOnHover
      theme="dark"
    />
    </>
  );
};

export default SignUpForm;
